import {clearAssistantSession} from 'app/pages/chatbot/modes/assistant/assistantChatSession'
import {getTenantCustomDomain} from 'common/HelperFunctions'
import Spinner from 'common/components/Spinner'
import {useCallback, useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate} from 'react-router'
import {RootState} from 'setup'
import {REDUX_DESTROY_SESSION} from 'setup/redux/Actions/SignIn/ActionTypes/ReduxActionTypes'
import {logout} from 'setup/redux/Actions/SignIn/logout'

export function Logout() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [isLoggingOut, setIsLoggingOut] = useState(true)
  const hasInitiated = useRef(false)
  // Admission applicants sign in on /admissions/auth; the portal login rejects
  // them (inactive student), which read as "my password stopped working".
  // Captured now: REDUX_DESTROY_SESSION clears it before we navigate.
  const isAdmissionUser = useSelector<RootState>(
    ({userReducer}) => (userReducer as any).isAdmissionUser
  ) as boolean
  const loginPath = useRef(isAdmissionUser ? '/admissions/auth' : '/auth/login')

  // Same path for success and failure: with an expired token the logout call
  // 401s too, so the failure branch is the normal case here.
  //
  // Order matters. The `logout` sessionStorage flag gates every authenticated
  // request in SagaHelper, and it is only safe to drop it once the dead token
  // is out of the store — otherwise a background caller (socket logger, layout
  // bootstrap) fires with it, 401s, and bounces the tab back to /logout. It is
  // also deliberately *not* released from an effect cleanup: a re-render or an
  // early unmount used to drop the guard while the token was still live. The
  // 10s timeout below is the backstop that guarantees it always gets released.
  const finishLogout = useCallback(() => {
    dispatch({
      type: REDUX_DESTROY_SESSION,
    })
    clearAssistantSession()
    setIsLoggingOut(false)
    navigate(loginPath.current, {replace: true})
    sessionStorage.removeItem('logout')
  }, [dispatch, navigate])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isLoggingOut) {
        finishLogout()
      }
    }, 10000)

    return () => clearTimeout(timeoutId)
  }, [isLoggingOut, finishLogout])

  useEffect(() => {
    if (hasInitiated.current) return
    hasInitiated.current = true

    sessionStorage.setItem('logout', 'true')
    dispatch(logout(finishLogout, finishLogout))
  }, [dispatch, finishLogout])

  if (!isLoggingOut) {
    return null
  }

  return (
    <div className='d-flex flex-column align-items-center justify-content-center vh-100 bg-body'>
      <Spinner type='grow' size='lg' color='primary' />

      <iframe
        id='logout-iframe'
        title='Logout frame'
        src={`//${getTenantCustomDomain()}/site/logout`}
        className='position-absolute top-0 start-0 h-100 w-100 z-index-n2'
        style={{
          border: 'none',
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
        aria-hidden='true'
      />
    </div>
  )
}
